import React from 'react';
import { Link } from 'react-router-dom';
import { useVersion } from '../contexts/VersionContext';
import useSectionViewTracking from '../hooks/useSectionViewTracking';
import styles from './GameInfo.module.css';

/** Split card copy on blank lines; HTML in a single <p> collapses \n to spaces. */
function featureBodyParagraphs(body) {
  return body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
}

/**
 * Desktop copy matches pre–mobile-optimization layout (unchanged for viewports > 768px).
 * Mobile copy is the density / line-break pass for ≤768px only.
 */
const featureCards = [
  {
    id: 1,
    anchor: 'different-strengths',
    image: '/images/webp/Card1-TraitCharacters.webp',
    image560: '/images/webp/Card1-TraitCharacters_560w.webp',
    title: 'Different Strengths Shape Each Civilization',
    bodyDesktop:
      'Each civilization approaches growth, expansion, and progress differently. From the very first turn, you begin leaning into the strengths that define your path. Those strengths make certain paths more effective. But committing to them means falling behind in others. The more you lean in, the more your civilization begins to specialize.',
    bodyMobile:
      'Each civilization pushes you toward certain strengths, making some paths more effective than others. The more you lean into one, the more you fall behind elsewhere.',
  },
  {
    id: 2,
    anchor: 'different-beginnings',
    image: '/images/webp/card2_HBSacks.webp',
    image560: '/images/webp/card2_HBSacks_560w.webp',
    title: 'No Two Games Begin the Same',
    bodyDesktop:
      'Each play begins with a different setup. Early resources and starting conditions don’t just vary, they push you toward certain decisions right from the start. Those first choices shape what you can build next, and from there, each game begins to unfold differently.',
    bodyMobile:
      'Each game starts with different conditions that shape your early decisions. Those first choices quickly lead to different paths and outcomes.',
  },
  {
    id: 3,
    anchor: 'core-challenge',
    image: '/images/webp/Card4-abstract.webp',
    image560: '/images/webp/Card4-abstract_560w.webp',
    title: "You Can\u2019t Do Everything",
    bodyDesktop:
      `Every turn, you\u2019ll see more than one strong direction. You can expand production, reduce action costs, improve trade efficiency, or race to a Wonder that unlocks a powerful advantage. Some build momentum over time, some move you ahead immediately, and some only work if you get there before anyone else.\n\nYou can\u2019t do all of them. The real question isn\u2019t what to do.`,
    emphasisLine: `It\u2019s what to do now, what can wait, and what you\u2019re willing to risk or let go.`,
    bodyMobile:
      `Every turn presents multiple strong options, but they do not lead to the same result. The challenge is deciding what to do now, what can wait, and what you\u2019re willing to give up.`,
    featured: true,
    microLabel: 'The Core Challenge',
  },
  {
    id: 4,
    anchor: 'government-choice',
    image: '/images/webp/card3_RepVsMon.webp',
    image560: '/images/webp/card3_RepVsMon_560w.webp',
    title: 'Power Takes a Form',
    bodyDesktop:
      'At a pivotal moment, you decide how your civilization will be ruled. Republic and Monarchy offer different strengths and encourage different ways to lead. Choosing one strengthens certain ambitions, but naturally pulls your civilization away from others.',
    bodyMobile:
      'At a key moment, you choose how your civilization is governed. That choice strengthens certain ambitions while pulling you away from others.',
  },
  {
    id: 5,
    anchor: 'exclusive-wonders',
    image: '/images/webp/card5_Wonders.webp',
    image560: '/images/webp/card5_Wonders_560w.webp',
    title: 'Build What Cannot Be Rebuilt',
    bodyDesktop:
      'Wonders shape more than your civilization. They shape the game. They can increase production, reduce costs, or open up entirely different strategic paths.\n\nWhen you build one, you’re not just gaining an advantage, you’re taking that path off the table for everyone else. And that changes things. Strategies built around it disappear. Plans shift.',
    bodyMobile:
      'Wonders provide powerful advantages and open new strategic paths. But once built, they are no longer available, forcing others to adapt.',
  },
];

const closingGameplayPillar = {
  title: 'Rivalry Without Combat',
};

export default function GameInfo({ coreFirst = false, hideFacts = false }) {
  const { isImprovedVersion } = useVersion();
  const aboutSectionRef = useSectionViewTracking('about_the_game');
  const featuresSectionRef = useSectionViewTracking('how_civilization_takes_shape');
  const orderedFeatureCards = coreFirst
    ? [featureCards[2], featureCards[0], featureCards[1], featureCards[3], featureCards[4]]
    : featureCards;

  return (
    <section className={`${styles.gameInfo} ${isImprovedVersion ? styles.gameInfoImproved : ''} ${coreFirst ? styles.coreFirst : ''}`}>
      {/* Block A: About the Game */}
      <div ref={aboutSectionRef} className={styles.blockA} id="about">
        <div className={styles.container}>
          <div className={styles.content}>
            <h2 className={styles.heading}>ABOUT THE GAME</h2>
            {/* Desktop: original single block (hidden ≤768px) */}
            <p className={`${styles.description} ${styles.gameInfoCopyDesktop}`}>
              <Link to="/inceptum" className={styles.inlineLink}><strong>INCEPTUM: Dawn of Civilizations</strong></Link> is an immersive civilization-building strategy board game where you&apos;ll develop technologies, expand your cities, and race for Wonders while navigating shared opportunities alongside your rivals.
              <br /><br />
              There’s no combat here. Victory comes from timing, choosing where to focus, and adapting to what other players are doing.
            </p>
            {/* Mobile: stacked paragraphs (hidden &gt;768px) */}
            <div className={`${styles.description} ${styles.gameInfoCopyMobile}`}>
              <p className={styles.descriptionPara}>
                <Link to="/inceptum" className={styles.inlineLink}><strong>INCEPTUM: Dawn of Civilizations</strong></Link> is an immersive civilization-building strategy board game where you&apos;ll develop technologies, expand your cities, and race for Wonders while navigating shared opportunities alongside your rivals.
              </p>
              <p className={styles.descriptionPara}>
                There’s no combat here. Victory comes from timing, choosing where to focus, and adapting to what other players are doing.
              </p>
            </div>
            {!hideFacts && <div className={styles.gameInfoBoxes}>
              <div className={styles.infoBox}>
                <div className={styles.iconWrapper}>
                  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoValue}>2-4</span>
                  <span className={styles.infoLabel}>Players</span>
                </div>
              </div>

              <div className={styles.infoBox}>
                <div className={styles.iconWrapper}>
                  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoValue}>60-120</span>
                  <span className={styles.infoLabel}>Minutes</span>
                </div>
              </div>

              <div className={styles.infoBox}>
                <div className={styles.iconWrapper}>
                  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoValue}>12+</span>
                  <span className={styles.infoLabel}>Ages</span>
                </div>
              </div>
            </div>}
          </div>
        </div>
      </div>

      {/* Block B: Features - alternating text/image rows */}
      <div ref={featuresSectionRef} className={styles.blockB} id="features">
        <div className={styles.featuresContainer}>
          <div className={styles.featuresBoxContainer}>
            <div className={styles.featuresHeader}>
              <h3 className={styles.featuresHeading}>A Civilization Game That Plays Differently.</h3>
              <p className={`${styles.featuresIntro} ${styles.gameInfoCopyDesktop}`}>
                RIVALRY WITHOUT COMBAT. HIGH VARIABILITY.
              </p>
              <p className={`${styles.featuresIntro} ${styles.featuresIntroMobile} ${styles.gameInfoCopyMobile}`}>
                RIVALRY WITHOUT COMBAT. HIGH VARIABILITY.
              </p>
            </div>

            <div className={styles.featuresFlow}>
              {orderedFeatureCards.map((card, index) => {
                if (card.featured) {
                  return (
                    <div key={card.id} id={`feature-${card.anchor}`} className={styles.featuredBlock}>
                      <div className={styles.featuredImageWrap}>
                        <img src={card.image560} srcSet={`${card.image560} 560w, ${card.image} 1120w`} sizes="(max-width: 768px) 100vw, 500px" alt={card.title} width={1120} height={752} loading="lazy" />
                      </div>
                      <div className={styles.featuredContent}>
                        {card.microLabel && (
                          <span className={styles.microLabel}>{card.microLabel}</span>
                        )}
                        <h4 className={styles.featuredTitle}>{card.title}</h4>
                        <div className={styles.featureDivider} aria-hidden="true" style={{ margin: '0.75rem auto 1rem' }} />
                        <div className={styles.gameInfoCopyDesktop}>
                          {featureBodyParagraphs(card.bodyDesktop).map((para, i) => (
                            <p key={i} className={styles.featuredBody}>{para}</p>
                          ))}
                          {card.emphasisLine && (
                            <p className={styles.featuredEmphasisLine}>{card.emphasisLine}</p>
                          )}
                        </div>
                        <div className={styles.gameInfoCopyMobile}>
                          {featureBodyParagraphs(card.bodyMobile).map((para, i) => (
                            <p key={i} className={styles.featuredBody}>{para}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                const isTextLeft = index % 2 === 0;
                return (
                  <div
                    key={card.id}
                    id={`feature-${card.anchor}`}
                    className={`${styles.featureRow} ${isTextLeft ? styles.textLeft : styles.textRight}`}
                  >
                    <div className={styles.featureText}>
                      <h4 className={styles.featureTitle}>{card.title}</h4>
                      <div className={styles.featureDivider} aria-hidden="true"></div>
                      <div className={styles.gameInfoCopyDesktop}>
                        {featureBodyParagraphs(card.bodyDesktop).map((para, i) => (
                          <p key={i} className={styles.featureBody}>{para}</p>
                        ))}
                      </div>
                      <div className={styles.gameInfoCopyMobile}>
                        {featureBodyParagraphs(card.bodyMobile).map((para, i) => (
                          <p key={i} className={styles.featureBody}>{para}</p>
                        ))}
                      </div>
                    </div>
                    <div className={styles.featureImage}>
                      <img src={card.image560} srcSet={`${card.image560} 560w, ${card.image} 1120w`} sizes="(max-width: 768px) 100vw, 500px" alt={card.title} width={1120} height={752} loading="lazy" />
                    </div>
                  </div>
                );
              })}

              {/* Same hero treatment as featured “You Can’t Do Everything” block - text only */}
              <div id="rivalry-without-combat" className={styles.featuredBlock}>
                <div className={styles.featuredContent}>
                  <h4 className={styles.featuredTitle}>{closingGameplayPillar.title}</h4>
                  <div className={styles.featureDivider} aria-hidden="true" style={{ margin: '0.75rem auto 1rem' }} />
                  <div className={styles.gameInfoCopyDesktop}>
                    <p className={styles.featuredBody}>
                      There is no combat in <Link to="/inceptum" className={styles.inlineLinkFeatured}><strong>INCEPTUM: Dawn of Civilizations</strong></Link>, but you're constantly reading the table.
                    </p>
                    <p className={styles.featuredBody}>
                      What are others building toward? What have they unlocked? Are they hoarding resources, and for what? What key pieces does their plan rely on? Should you take it before they do, or stay on your own path?
                    </p>
                    <p className={styles.featuredBody}>
                      Taking it first comes at a cost. Both of you lose momentum, and someone else pulls ahead.
                    </p>
                  </div>
                  <div className={styles.gameInfoCopyMobile}>
                    <p className={styles.featuredBody}>
                      There is no combat in <Link to="/inceptum" className={styles.inlineLinkFeatured}><strong>INCEPTUM</strong></Link>, but players constantly read each other and compete for key opportunities. Choosing to take something before another player can slow both of you down, while someone else who stays on course pulls ahead.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
